import { buildSegments, formatDateTime, parseDateTime, cleanText, normalizePlate } from '../../../services/possessionMatcher';
import { isEjarClosed } from '../../../services/ejarOpenClose';

const firstText = (row, ...keys) => {
    for (const k of keys) {
        const v = cleanText(row[k]);
        if (v) return v;
    }
    return '';
};

const contractKey = (v) => cleanText(v).toUpperCase();

/**
 * Multi-car analysis over raw E-Check rows.
 *
 * Possession segmentation is delegated to the shared `buildSegments` (one
 * segment per (contract, plate) check-out -> check-in pair, so A -> B -> A is
 * three segments and a re-inspection never splits a session). Multi-Car has no
 * contract records, so `contracts` is [] and the first segment simply starts at
 * the first check-out.
 *
 * Returns { results, summary }:
 *   results  = [{ contractNo, customerName, carsCount, carDetails: [{ plateNumber, carModel, from, to, branch }] }]
 *   summary  = { totalRows, totalContracts, singleCarContracts, multiCarContracts, skipped }
 */
export const analyzeMultiCarContracts = (data, now = new Date()) => {
    const skipped = { noContract: 0, noPlate: 0, noDate: 0 };
    const rows = [];
    const byContract = new Map(); // contractNo -> { customerName, byPlate: Map<plate, rows[]> }

    (data || []).forEach(row => {
        const contractNo = contractKey(row['Contract No.'] ?? row['Contract No'] ?? row['Agreement']);
        const plateRaw = firstText(row, 'Plate Number', 'Plate No.', 'Plate No');
        const plate = normalizePlate(plateRaw);
        const when = parseDateTime(row['Transaction Date'] ?? row['Revenue Date'] ?? row['Date']);

        if (!contractNo) { skipped.noContract++; return; }
        if (!plate) { skipped.noPlate++; return; }
        if (!when) { skipped.noDate++; return; }

        const model = firstText(row, 'Car Model', 'Model', 'model', 'Vehicle');
        const branch = firstText(row, 'Branch', 'branch', 'Pick-up Branch');

        rows.push({
            'Contract No.': contractNo,
            'Plate Number': plateRaw,
            'Transaction Date': when,
            'Check Type': row['Check Type'] ?? '',
            Branch: branch,
            // the order of two checks in the same minute (possessionMatcher.buildSegments - CC 79361, 3 Oct 2026)
            'E-Check Number': row['E-Check Number'] ?? '',
            check_id: row.check_id,
        });

        let contract = byContract.get(contractNo);
        if (!contract) {
            contract = { customerName: '', byPlate: new Map() };
            byContract.set(contractNo, contract);
        }
        if (!contract.customerName) contract.customerName = firstText(row, 'Customer Name', 'Customer', 'customer', 'Name');
        if (!contract.byPlate.has(plate)) contract.byPlate.set(plate, []);
        contract.byPlate.get(plate).push({ when, model });
    });

    for (const contract of byContract.values()) {
        for (const list of contract.byPlate.values()) list.sort((a, b) => a.when - b.when);
    }

    // Segments grouped by contract (buildSegments returns them sorted by contract, then start)
    const segmentsOf = new Map();
    for (const seg of buildSegments(rows, [], now)) {
        if (!segmentsOf.has(seg.contractNo)) segmentsOf.set(seg.contractNo, []);
        segmentsOf.get(seg.contractNo).push(seg);
    }

    const modelFor = (contract, seg) => {
        const list = contract.byPlate.get(seg.plate) || [];
        const inside = list.find(r => r.model && r.when >= seg.start && r.when <= seg.end);
        if (inside) return inside.model;
        const any = list.find(r => r.model);
        return any ? any.model : '';
    };

    const results = [];
    for (const [contractNo, contract] of byContract) {
        const segments = segmentsOf.get(contractNo) || [];
        if (!isMultiCar(segments)) continue;
        results.push({
            contractNo,
            customerName: contract.customerName,
            carsCount: segments.length,
            carDetails: segments.map(seg => ({
                plateNumber: seg.plateRaw,
                carModel: modelFor(contract, seg),
                from: formatDateTime(seg.start),
                to: seg.open ? 'Open' : formatDateTime(seg.end),
                branch: seg.branch,
            })),
        });
    }
    results.sort((a, b) => a.contractNo.localeCompare(b.contractNo));

    const totalContracts = byContract.size;
    const summary = {
        totalRows: (data || []).length,
        totalContracts,
        singleCarContracts: totalContracts - results.length,
        multiCarContracts: results.length,
        skipped,
    };
    return { results, summary };
};

const ejarMs = (iso) => {
    const t = Date.parse(iso || '');
    return Number.isFinite(t) ? t : null;
};

/**
 * The multi-car contracts with each contract's open and close by the owner's rule (6 Oct 2026, the History Lens one -
 * services/ejarOpenClose): the first car's "from" = Ejar's pick-up when it is before the first E-Check (Ejar opens
 * first), the last car's "to" = Ejar's drop-off once Ejar has it closed when it is before the last close check (or the
 * car still reads "Open"). ``records``: the store's contract records (contract_no, pickup_date / dropoff_date ISO,
 * status). Car swaps in between keep the E-Check times. ``sinceMs``: where the E-Checks read start (the last 12
 * months) - a contract opened before it had other cars before the first one read, whose "from" stays (C2401ARE046282,
 * opened 18/01/2024 on J 96735: M 53751 of Nov 2025 is not its first car). Pure: the results given are copied.
 */
export const withEjarOpenClose = (results, records, sinceMs = null) => {
    const byNo = new Map((records || []).filter((v) => v?.contract_no).map((v) => [contractKey(v.contract_no), v]));
    return (results || []).map((r) => {
        const v = byNo.get(contractKey(r.contractNo));
        if (!v || !Array.isArray(r.carDetails) || !r.carDetails.length) return r;
        const cars = r.carDetails.map((c) => ({ ...c }));
        const first = cars[0];
        const last = cars[cars.length - 1];
        const pick = ejarMs(v.pickup_date);
        const from = parseDateTime(first.from);
        if (pick !== null && (sinceMs === null || pick >= sinceMs) && (!from || pick < from.getTime())) first.from = formatDateTime(new Date(pick));
        const dropAt = isEjarClosed(v) ? ejarMs(v.dropoff_date) : null;
        const drop = dropAt !== null && dropAt <= Date.now() ? dropAt : null;      // never a date still to come
        if (drop !== null) {
            const to = last.to === 'Open' ? null : parseDateTime(last.to);
            const start = parseDateTime(last.from);
            if ((!to || drop < to.getTime()) && (!start || drop >= start.getTime())) last.to = formatDateTime(new Date(drop));
        }
        return { ...r, carDetails: cars };
    });
};

/**
 * A contract is multi-car when it has at least two possession segments whose
 * plates are not all identical, or whose plates repeat (A -> B -> A).
 */
function isMultiCar(segments) {
    if (segments.length < 2) return false;
    const plates = new Set(segments.map(s => s.plate));
    const allIdentical = plates.size === 1;
    const anyRepeats = plates.size < segments.length;
    return !allIdentical || anyRepeats;
}
